if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.stylesReady = Promise.all(
        Array.from(document.querySelectorAll('link[data-cart-drawer-style]'), (stylesheet) =>
          this.constructor.loadStylesheet(stylesheet)
        )
      );
      // A background failure is handled when the visitor tries to open the cart.
      this.stylesReady.catch(() => {});
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      }, { signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close], #CartDrawer-Overlay')) this.close();
      }, { signal });
      this.addEventListener('keydown', (event) => this.keepFocusInDrawer(event), { signal });
      this.setHeaderCartIconAccessibility(signal);
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(this)) this.open();
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (this.contains(event.target)) this.open();
      }, { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.openRequest || this.classList.contains('active')) this.close();
      else {
        cancelAnimationFrame(this.openFrame);
        cancelAnimationFrame(this.focusFrame);
        clearTimeout(this.focusTimer);
      }
    }

    static loadStylesheet(stylesheet) {
      if (stylesheet.cartDrawerStylePromise) return stylesheet.cartDrawerStylePromise;
      stylesheet.cartDrawerStylePromise = new Promise((resolve, reject) => {
        const loaded = () => {
          stylesheet.media = 'all';
          cleanup();
          resolve();
        };
        const failed = () => {
          cleanup();
          reject(new Error('Cart drawer stylesheet unavailable'));
        };
        const cleanup = () => {
          stylesheet.removeEventListener('load', loaded);
          stylesheet.removeEventListener('error', failed);
          clearTimeout(timeout);
        };
        const timeout = setTimeout(failed, 15000);
        stylesheet.addEventListener('load', loaded, { once: true });
        stylesheet.addEventListener('error', failed, { once: true });
        // Cached styles can finish before this deferred script executes.
        if (stylesheet.sheet) loaded();
      });
      return stylesheet.cartDrawerStylePromise;
    }

    keepFocusInDrawer(event) {
      const dialog = this.querySelector('.drawer__inner');
      if (event.key !== 'Tab' || !this.classList.contains('active') || event.target.closest('[role="dialog"]') !== dialog) return;
      // Dawn's helper includes hidden empty-state and recommendation controls.
      // Resolve the visible controls again after every cart or carousel update.
      const controls = getFocusableElements(dialog).filter((element) => element.tabIndex >= 0 && element.getClientRects().length);
      event.preventDefault();
      event.stopPropagation();
      if (!controls.length) return dialog.focus();
      const position = controls.indexOf(document.activeElement);
      const next = position < 0
        ? (event.shiftKey ? controls.length - 1 : 0)
        : (position + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
      controls[next].focus();
    }

    setHeaderCartIconAccessibility(signal) {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      }, { signal });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code === 'Space') {
          event.preventDefault();
          this.open(cartLink);
        }
      }, { signal });
    }

    async open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.openRequest || this.classList.contains('active')) return;
      const request = { trigger: this.activeElement };
      this.openRequest = request;
      this.setAttribute('aria-busy', 'true');
      request.trigger?.setAttribute('aria-busy', 'true');
      try {
        await this.stylesReady;
        if (!this.isConnected || this.openRequest !== request) return;
        this.openFrame = requestAnimationFrame(() => {
          if (!this.isConnected || this.openRequest !== request) return;
          this.clearPendingOpen();
          this.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          this.querySelector('cart-drawer-items')?.dispatchViewEvent();
          // Allow the newly visible drawer to paint before moving keyboard focus.
          this.focusFrame = requestAnimationFrame(() => {
            if (!this.isConnected || !this.classList.contains('active')) return;
            const dialog = this.querySelector('.drawer__inner');
            trapFocus(dialog, dialog);
          });
        });
      } catch {
        if (!this.isConnected || this.openRequest !== request) return;
        this.clearPendingOpen();
        window.location.assign(routes.cart_url);
      }
    }

    clearPendingOpen() {
      this.openRequest?.trigger?.removeAttribute('aria-busy');
      this.openRequest = null;
      this.removeAttribute('aria-busy');
    }

    close() {
      cancelAnimationFrame(this.openFrame);
      cancelAnimationFrame(this.focusFrame);
      clearTimeout(this.focusTimer);
      this.clearPendingOpen();
      this.classList.remove('active');
      const returnFocus = this.activeElement?.isConnected
        ? this.activeElement
        : document.querySelector('#cart-icon-bubble');
      removeTrapFocus(returnFocus);
      document.body.classList.remove('overflow-hidden');
    }

    renderContents(parsedState, shouldOpen = true) {
      if (!parsedState.sections?.['cart-drawer']) return this.refreshFromServer(shouldOpen);
      const drawerHTML = this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer');
      this.classList.toggle('is-empty', drawerHTML.classList.contains('is-empty'));
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? this.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = parsedState.sections[section.id];
        if (sectionElement && html) sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
      if (shouldOpen) this.open();
      if (this.classList.contains('active')) {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.classList.contains('active')) return;
          const dialog = this.querySelector('.drawer__inner');
          trapFocus(dialog, dialog);
        }, 0);
      }
    }

    async refreshFromServer(shouldOpen = false) {
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
      const response = await fetch(url);
      if (!response.ok) throw new Error(window.cartStrings.error);
      const sections = await response.json();
      if (!sections['cart-drawer']) throw new Error(window.cartStrings.error);
      return this.renderContents({ sections }, shouldOpen);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return this.getSectionDOM(html, selector)?.innerHTML || '';
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  });
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', class CartDrawerItems extends CartItems {
    onCartUpdate(event) {
      // ProductForm renders its bundled sections after publishing the update.
      if (event?.source === 'product-form') return;
      return this.closest('cart-drawer')?.refreshFromServer().catch((error) => console.error(error));
    }

    updateQuantity(...args) {
      const drawer = this.closest('cart-drawer');
      if (drawer?.mutationPending) return;
      if (drawer) drawer.mutationPending = true;
      const checkout = drawer?.querySelector('#CartDrawer-Checkout');
      if (checkout) checkout.disabled = true;
      return super.updateQuantity(...args).finally(() => {
        if (drawer) drawer.mutationPending = false;
        const updatedCheckout = drawer?.querySelector('#CartDrawer-Checkout');
        if (updatedCheckout) updatedCheckout.disabled = drawer.classList.contains('is-empty');
      });
    }

    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }
  });
}
